import React, { useState } from "react";
import { X, CheckCircle, AlertCircle, Copy } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import { ContractStatusEnum, RefundRequestTypeEnum } from "../../modules/shared/model";
import { createRefundRequestAction } from "../../redux/Actions/RefundRequestActions";
import { EscrowStatus, EscrowStatusEnum } from "../../services/escrowAPI";
import { useTranslation } from "react-i18next";

interface ContractCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (reason: string) => void;
  contractTitle?: string;
  contractId?: string;
  contract?: any; // Contract object with escrows array
  loading?: boolean;
}

const ContractCompletionModal: React.FC<ContractCompletionModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  contractTitle = "Contract",
  contractId,
  contract,
  loading = false
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [showRefundRequest, setShowRefundRequest] = useState(false);
  const [createRefundRequest, setCreateRefundRequest] = useState(true);
  const [refundType, setRefundType] = useState<RefundRequestTypeEnum>(RefundRequestTypeEnum.Full);
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Function to validate refund amount against released escrow amounts
  const validateRefundAmount = (): boolean => {
    console.log("contract", contract);
    if (!contract?.escrows || !Array.isArray(contract.escrows)) {
      setValidationError(t("contractCompletionModal.escrowNotAvailable"));
      return false;
    }

    // Calculate total released amount from escrows
    const totalReleasedAmount = contract.escrows
      .filter((escrow: any) => {
        return Number(escrow.status.id) === EscrowStatus.FUNDED;
      })
      .reduce((sum: number, escrow: any) => {
        return sum + (Number(escrow.amount) || 0);
      }, 0);

    // For partial refunds, check if requested amount exceeds released amount
    if (refundType === RefundRequestTypeEnum.Partial && refundAmount) {
      if (refundAmount > totalReleasedAmount) {
        setValidationError(t("contractCompletionModal.refundExceedsReleased", { 
          refundAmount: refundAmount.toFixed(2), 
          releasedAmount: totalReleasedAmount.toFixed(2) 
        }));
        return false;
      }
    }

    // For full refunds, check if there's any released amount
    if (refundType === RefundRequestTypeEnum.Full && totalReleasedAmount === 0) {
      setValidationError(t("contractCompletionModal.noReleasedAmounts"));
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleComplete = async () => {
    if (reason.trim()) {
      // Validate refund amount if creating a refund request
      if (createRefundRequest) {
        if (!validateRefundAmount()) {
          return; // Stop execution if validation fails
        }

        setRefundLoading(true);
        try {
          // Calculate total released amount for full refunds
          let refundAmountToSave = null;
          if (refundType === RefundRequestTypeEnum.Partial) {
            refundAmountToSave = refundAmount;
          } else if (refundType === RefundRequestTypeEnum.Full) {
            // For full refunds, save the total released amount
            const totalReleasedAmount = contract.escrows
              .filter((escrow: any) => {
                return Number(escrow.status.id) === EscrowStatus.FUNDED;
              })
              .reduce((sum: number, escrow: any) => {
                return sum + (Number(escrow.amount) || 0);
              }, 0);
            refundAmountToSave = totalReleasedAmount;
          }

          await createRefundRequestAction({
            contract: { id: contract.id },
            type: { id: refundType },
            status: { id: 1 }, // Active status
            amount: refundAmountToSave,
            reason: refundReason || reason.trim()
          });
        } catch (error) {
          console.error("Failed to create refund request:", error);
          setValidationError(t("contractCompletionModal.refundRequestFailed"));
          setRefundLoading(false);
          return;
        } finally {
          setRefundLoading(false);
        }
      }

      onComplete(reason.trim());
    }
  };

  const handleClose = () => {
    setReason("");
    setShowRefundRequest(false);
    setCreateRefundRequest(false);
    setRefundType(RefundRequestTypeEnum.Full);
    setRefundReason("");
    setRefundAmount(null);
    setRefundLoading(false);
    setValidationError(null);
    setTermsAccepted(false);
    onClose();
  };

  const handleCopyReason = () => {
    setRefundReason(reason);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t("contractCompletionModal.title")}
                </h2>
                <p className="text-sm text-gray-600">
                  {contractTitle}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Information Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-blue-900 mb-1">
                    {t("contractCompletionModal.completionTitle")}
                  </h3>
                  <p className="text-sm text-blue-800">
                    {t("contractCompletionModal.completionMessage")}
                  </p>
                </div>
              </div>
            </div>

            {/* Reason Text Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("contractCompletionModal.closeReason")} *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("contractCompletionModal.closeReasonPlaceholder")}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={4}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("contractCompletionModal.minimumCharacters")}
              </p>
            </div>

            {/* Refund Request Section */}
            <div className="border-t pt-4">

              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="space-y-4">
                  {/* Checkbox to enable refund request creation */}
                  <div className="flex items-center gap-2">
                    {/* <input
                        type="checkbox"
                        id="createRefundRequest"
                        checked={createRefundRequest}
                        onChange={(e) => setCreateRefundRequest(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        disabled={loading || refundLoading}
                      /> */}
                    <label className="text-sm font-medium text-yellow-900">
                      {t("contractCompletionModal.createRefundRequest")}
                    </label>
                  </div>

                  {createRefundRequest && (
                    <div className="space-y-4 pt-2 border-t border-yellow-200">
                      {/* Refund Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-yellow-900 mb-2">
                          {t("contractCompletionModal.refundType")} *
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="refundType"
                              value={RefundRequestTypeEnum.Full}
                              checked={refundType === RefundRequestTypeEnum.Full}
                              onChange={(e) => {
                                setRefundType(Number(e.target.value) as RefundRequestTypeEnum);
                                setValidationError(null); // Clear validation error when type changes
                              }}
                              className="w-4 h-4 text-blue-600"
                              disabled={loading || refundLoading}
                            />
                            <span className="text-sm text-yellow-800">{t("contractCompletionModal.fullRefund")}</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="refundType"
                              value={RefundRequestTypeEnum.Partial}
                              checked={refundType === RefundRequestTypeEnum.Partial}
                              onChange={(e) => {
                                setRefundType(Number(e.target.value) as RefundRequestTypeEnum);
                                setValidationError(null); // Clear validation error when type changes
                              }}
                              className="w-4 h-4 text-blue-600"
                              disabled={loading || refundLoading}
                            />
                            <span className="text-sm text-yellow-800">{t("contractCompletionModal.partialRefund")}</span>
                          </label>
                        </div>
                      </div>

                      {/* Refund Amount (for partial refunds) */}
                      {refundType === RefundRequestTypeEnum.Partial && (
                        <div>
                          <label className="block text-sm font-medium text-yellow-900 mb-2">
                            {t("contractCompletionModal.refundAmount")}
                          </label>
                          <input
                            type="number"
                            value={refundAmount || ""}
                            onChange={(e) => {
                              setRefundAmount(e.target.value ? Number(e.target.value) : null);
                              setValidationError(null); // Clear validation error when user types
                            }}
                            placeholder={t("contractCompletionModal.refundAmountPlaceholder")}
                            className="w-full p-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={loading || refundLoading}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      )}

                      {/* Refund Reason */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-yellow-900">
                            {t("contractCompletionModal.refundReason")}
                          </label>
                          <button
                            type="button"
                            onClick={handleCopyReason}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                            disabled={loading || refundLoading || !reason.trim()}
                          >
                            <Copy className="h-3 w-3" />
                            {t("contractCompletionModal.copyFromClose")}
                          </button>
                        </div>
                        <textarea
                          value={refundReason}
                          onChange={(e) => setRefundReason(e.target.value)}
                          placeholder={t("contractCompletionModal.refundReasonPlaceholder")}
                          className="w-full p-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          rows={3}
                          disabled={loading || refundLoading}
                        />
                      </div>

                      {/* Validation Error Display */}
                      {validationError && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-red-800">
                            <p className="font-medium mb-1">{t("contractCompletionModal.validationError")}:</p>
                            <p>{validationError}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                </div>
              </Card>
            </div>

            {/* Terms & Conditions Checkbox */}
            <div className="flex items-start gap-2 pt-2">
              <input
                type="checkbox"
                id="termsAccepted"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                disabled={loading || refundLoading}
              />
              <label htmlFor="termsAccepted" className="text-sm text-gray-700">
                {t("contractCompletionModal.agreeToTerms")}{" "}
                <a
                  href="/terms-conditions#dispute"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  {t("contractCompletionModal.termsAndConditions")}
                </a>{" "}
                {t("contractCompletionModal.regardingDispute")}
              </label>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={loading || refundLoading}
            >
              {t("contractCompletionModal.cancel")}
            </Button>
            <Button
              className="flex-1"
              onClick={handleComplete}
              disabled={loading || refundLoading || reason.trim().length < 10 || !termsAccepted}
            >
              {(loading || refundLoading) ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {refundLoading ? t("contractCompletionModal.processingRefund") : t("contractCompletionModal.closing")}
                </div>
              ) : (
                t("contractCompletionModal.closeContract")
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractCompletionModal;
